import React from 'react';
import styled from '@emotion/styled';
import { useParams, Link } from 'react-router-dom';
import Button from '../../components/common/Button';
import authService from '../../services/api/auth';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from '../../components/helpers/recaptchaUtils';

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #051937, #0a274f);
  padding: 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 18px;
  padding: 32px 36px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
`;

const Title = styled.h1`
  margin: 0 0 12px;
  font-size: 1.8rem;
  color: #0c2b4a;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: #4a5f78;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;

  input {
    width: 100%;
    padding: 14px 16px;
    border-radius: 10px;
    border: 1px solid #d9e1ec;
    font-size: 1rem;
    background-color: #f5f8fc;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      outline: none;
      border-color: #1d6fa3;
      box-shadow: 0 0 0 3px rgba(29, 111, 163, 0.15);
      background-color: #ffffff;
    }
  }
`;

const Message = styled.p`
  margin: 8px 0 0;
  font-weight: 600;
  color: ${({ type }) =>
    type === 'error' ? '#c0392b' : type === 'success' ? '#1d8348' : '#4a5f78'};
  text-align: center;
`;

const InlineLink = styled(Link)`
  color: #1d6fa3;
  text-decoration: underline;
`;

const Checklist = styled.ul`
  list-style: none;
  padding: 0;
  margin: -4px 0 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChecklistItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: ${({ valid }) => (valid ? '#1d8348' : '#4a5f78')};
`;

const Indicator = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${({ valid }) => (valid ? '#1d8348' : '#d9e1ec')};
  background-color: ${({ valid }) => (valid ? '#1d8348' : 'transparent')};
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PASSWORD_POLICY = {
  minLength: 10,
  regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/,
};

const buildPasswordChecklist = (password = '') => {
  return [
    {
      id: 'length',
      label: `Al menos ${PASSWORD_POLICY.minLength} caracteres.`,
      valid: password.length >= PASSWORD_POLICY.minLength,
      message: `La contraseña debe tener al menos ${PASSWORD_POLICY.minLength} caracteres.`,
    },
    {
      id: 'lowercase',
      label: 'Incluye una letra minúscula.',
      valid: /[a-z]/.test(password),
      message: 'La contraseña debe contener al menos una letra minúscula.',
    },
    {
      id: 'uppercase',
      label: 'Incluye una letra mayúscula.',
      valid: /[A-Z]/.test(password),
      message: 'La contraseña debe contener al menos una letra mayúscula.',
    },
    {
      id: 'number',
      label: 'Incluye un número.',
      valid: /\d/.test(password),
      message: 'La contraseña debe contener al menos un número.',
    },
    {
      id: 'special',
      label: 'Incluye un carácter especial.',
      valid: /[^A-Za-z0-9]/.test(password),
      message: 'La contraseña debe contener al menos un carácter especial.',
    },
  ];
};

const validatePassword = (password = '') => {
  const checklist = buildPasswordChecklist(password);
  const failedRule = checklist.find((rule) => !rule.valid);
  if (failedRule) {
    return {
      valid: false,
      message: failedRule.message,
    };
  }
  return { valid: true };
};

const ResetPassword = () => {
  const { token } = useParams();
  const [status, setStatus] = React.useState('checking');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [message, setMessage] = React.useState('Validando enlace...');
  const [messageType, setMessageType] = React.useState('info');
  const [loading, setLoading] = React.useState(false);
  const passwordChecklist = React.useMemo(() => buildPasswordChecklist(password), [password]);
  const getCaptchaToken = useRecaptchaToken('reset_password_confirm');
  const captchaRequired = isCaptchaRequired();

  React.useEffect(() => {
    let isMounted = true;
    const validateToken = async () => {
      if (!token) {
        setStatus('invalid');
        setMessage('El enlace no es válido.');
        setMessageType('error');
        return;
      }
      try {
        await authService.validatePasswordResetToken(token);
        if (!isMounted) return;
        setStatus('ready');
        setMessage('');
      } catch (error) {
        if (!isMounted) return;
        setStatus('invalid');
        const serverMessage = error?.response?.data?.message;
        setMessage(serverMessage || 'Este enlace expiró o ya fue utilizado.');
        setMessageType('error');
      }
    };
    validateToken();
    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status !== 'ready') return;

    const result = validatePassword(password);
    if (!result.valid) {
      setMessage(result.message);
      setMessageType('error');
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Las contraseñas no coinciden.');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      const captcha = await getCaptchaToken({ skip: !captchaRequired });
      if (captchaRequired && !captcha) {
        setMessage(RECAPTCHA_SECURITY_ERROR);
        setMessageType('error');
        setLoading(false);
        return;
      }

      const response = await authService.resetPasswordWithToken({
        token,
        password,
        confirmPassword,
        ...(captchaRequired ? { captcha } : {}),
      });
      setStatus('success');
      setMessage(response?.data?.message || 'Actualizamos tu contraseña. Puedes iniciar sesión.');
      setMessageType('success');
    } catch (error) {
      const serverMessage = error?.response?.data?.message;
      setMessage(serverMessage || 'No pudimos actualizar tu contraseña. Intenta nuevamente.');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (status === 'checking') {
      return <Message type="info">Validando enlace...</Message>;
    }

    if (status === 'invalid') {
      return (
        <>
          <Message type="error">{message}</Message>
          <Subtitle style={{ textAlign: 'center', marginTop: 16 }}>
            Solicita un nuevo enlace desde la opción <strong>Olvidé mi contraseña</strong> en{' '}
            <InlineLink to="/login">login</InlineLink>.
          </Subtitle>
        </>
      );
    }

    if (status === 'success') {
      return (
        <>
          <Message type="success">{message}</Message>
          <Button
            text="Ir al login"
            type="button"
            onClick={() => (window.location.href = '/login')}
            style={{ marginTop: 24, width: '100%' }}
          />
        </>
      );
    }

    return (
      <>
        <Subtitle>Crea una nueva contraseña segura para tu cuenta.</Subtitle>
        <Form onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder="Nueva contraseña"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
          />
          <Checklist aria-live="polite">
            {passwordChecklist.map((rule) => (
              <ChecklistItem key={rule.id} valid={rule.valid}>
                <Indicator valid={rule.valid}>{rule.valid ? '✓' : ''}</Indicator>
                <span>{rule.label}</span>
              </ChecklistItem>
            ))}
          </Checklist>
          <input
            type="password"
            placeholder="Confirmar contraseña"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
          />
          <Button
            text="Actualizar contraseña"
            disabled={loading}
            type="submit"
            onClick={handleSubmit}
            style={{ width: '100%' }}
          />
        </Form>
        {message && <Message type={messageType}>{message}</Message>}
      </>
    );
  };

  return (
    <Page>
      <Card>
        <Title>Restablecer contraseña</Title>
        {renderContent()}
      </Card>
    </Page>
  );
};

export default ResetPassword;
